import { useState, Fragment } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Building2,
  MapPin,
  Calendar,
  Wallet,
  Truck,
  Package,
  HardHat,
  Receipt,
  Repeat,
  Plus,
  Bell,
  CheckCircle2,
  ExternalLink,
  IndianRupee,
  Clock,
  Briefcase,
  Layers,
  Sparkles,
  ChevronDown,
  ChevronRight,
  Edit2,
  Trash2,
} from 'lucide-react';
import { formatMoney, isAdmin, canSubmit, canAccessProject, type Permission } from '@vns/core';
import { useSession } from '../lib/session';
import { useProject, useLedger, type LedgerTab } from '../lib/project';
import Shell from '../components/Shell';
import EntryDrawer, { ENTRY_META, type EntryKind, type ExistingEntry } from '../components/EntryDrawers';
import BillDrawer from '../components/BillDrawer';
import UsageDrawer from '../components/UsageDrawer';
import SubcontractorTransactionDrawer from '../components/SubcontractorTransactionDrawer';
import ExpenseDrawer from '../components/ExpenseDrawer';
import StockTransferDrawer from '../components/StockTransferDrawer';
import DrawingUploadDrawer from '../components/DrawingUploadDrawer';
import ProjectDrawingsLedger from '../components/ProjectDrawingsLedger';
import ElevationUploadDrawer from '../components/ElevationUploadDrawer';
import ProjectElevationsLedger from '../components/ProjectElevationsLedger';
import { useDeleteEntry, useRestoreEntry } from '../lib/project-edit';
import {
  useSubcontractorTransactions,
  useDeleteSubcontractorTransaction,
  cleanDisplayRemarks,
  cleanWorkTypeName,
  type SubcontractorTxRow,
} from '../lib/transactions';
import Confirm from '../components/Confirm';
import {
  StatusChip, EmptyState, TableSkeleton, ErrorState, formatDate, useToast,
} from '../components/ui';

/**
 * The project workspace — six actions and the ledgers they write to.
 */
const ACTIONS: Array<{
  kind: EntryKind;
  tab: LedgerTab;
  permission: Permission;
  icon: typeof Wallet;
  label: string;
  adminOnly?: boolean;
}> = [
  { kind: 'payment',  tab: 'payments',  permission: 'add_client_payment',       icon: Wallet,  label: 'Client payment', adminOnly: true },
  { kind: 'purchase', tab: 'purchases', permission: 'add_material_purchase',    icon: Truck,   label: 'Supplier or material payment' },
  { kind: 'usage',    tab: 'usage',     permission: 'add_material_usage',       icon: Package, label: 'Material Used' },
  { kind: 'labour',   tab: 'labour',    permission: 'add_subcontractor_labour', icon: HardHat, label: 'Sub contractor payment' },
  { kind: 'expense',  tab: 'expenses',  permission: 'add_expense',              icon: Receipt, label: 'Add expenses' },
  { kind: 'transfer', tab: 'transfers', permission: 'add_stock_transfer',       icon: Repeat,  label: 'Site Transfer' },
];

/** Which entry form corrects each ledger. */
const TAB_KIND: Record<LedgerTab, EntryKind> = {
  payments: 'payment',
  purchases: 'purchase',
  usage: 'usage',
  labour: 'labour',
  expenses: 'expense',
  transfers: 'transfer',
  drawings: 'expense',
  elevations: 'expense',
};

const TABS: Array<{ key: LedgerTab; label: string; adminOnly?: boolean; icon: typeof Wallet }> = [
  { key: 'payments',   label: 'Payments', adminOnly: true, icon: Wallet },
  { key: 'purchases',  label: 'Material Purchases', icon: Truck },
  { key: 'usage',      label: 'Material Used', icon: Package },
  { key: 'labour',     label: 'Sub-contractor Labour', icon: HardHat },
  { key: 'expenses',   label: 'Expenses', icon: Receipt },
  { key: 'transfers',  label: 'Site Transfers', icon: Repeat },
  { key: 'drawings',   label: 'Project Drawings', icon: Layers },
  { key: 'elevations', label: '3D Elevation', icon: Sparkles },
];

export default function ProjectDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const actor = useSession((s) => s.actor);
  const admin = isAdmin(actor);

  const tabs = TABS.filter((t) => admin || !t.adminOnly);
  const [tab, setTab] = useState<LedgerTab>(() => tabs[0]?.key ?? 'purchases');
  const [entry, setEntry] = useState<EntryKind | null>(null);
  const [billOpen, setBillOpen] = useState(false);
  const [usageOpen, setUsageOpen] = useState(false);
  const [labourOpen, setLabourOpen] = useState(false);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [drawingOpen, setDrawingOpen] = useState(false);
  const [elevationOpen, setElevationOpen] = useState(false);
  
  const [editRow, setEditRow] = useState<ExistingEntry | null>(null);
  const [deleteRow, setDeleteRow] = useState<ExistingEntry | null>(null);
  const [subcontractorEditRow, setSubcontractorEditRow] = useState<SubcontractorTxRow | null>(null);
  const [subcontractorDeleteRow, setSubcontractorDeleteRow] = useState<SubcontractorTxRow | null>(null);
  const deleteSubcontractorTx = useDeleteSubcontractorTransaction();
  const removeEntry = useDeleteEntry(id);
  const restoreEntry = useRestoreEntry(id);
  const toast = useToast();

  async function doDeleteEntry() {
    if (!deleteRow) return;
    const kind = TAB_KIND[tab];
    const row = deleteRow;
    try {
      await removeEntry.mutateAsync({ kind, id: row.id });
      toast.showWithAction('Entry deleted', {
        label: 'Undo',
        run: () => restoreEntry.mutateAsync({ kind, id: row.id }),
      });
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not delete this entry', true);
    } finally {
      setDeleteRow(null);
      setEditRow(null);
    }
  }

  const { data: project, isLoading, error, refetch } = useProject(id);
  const available = ACTIONS.filter((a) => {
    if (a.adminOnly && !admin) return false;
    if (a.kind === 'transfer') return canAccessProject(actor, id);
    return canSubmit(actor, a.permission, id);
  });

  return (
    <Shell
      title={project?.name ?? 'Project'}
      crumb="Projects"
    >
      <button
        className="btn btn-auto btn-sm btn-ghost"
        style={{
          marginBottom: 16,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          color: 'var(--text-soft)',
          paddingLeft: 4
        }}
        onClick={() => navigate('/projects')}
      >
        <ArrowLeft size={14} />
        <span>Back to Projects</span>
      </button>

      {isLoading ? (
        <div className="card"><TableSkeleton rows={4} cols={4} /></div>
      ) : error ? (
        <div className="card"><ErrorState error={error} onRetry={() => void refetch()} /></div>
      ) : !project ? (
        <div className="card"><EmptyState icon="briefcase" title="Project not found" /></div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* Project Profile Card */}
          <div
            className="card"
            style={{
              padding: '20px 24px',
              marginBottom: 20,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 14 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <h2 style={{ fontSize: 21, fontWeight: 700, margin: 0 }}>{project.name}</h2>
                  <StatusChip value={project.status} />
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: 16,
                    flexWrap: 'wrap',
                    marginTop: 10,
                    fontSize: 13,
                    color: 'var(--text-soft)',
                    alignItems: 'center',
                  }}
                >
                  {admin ? (
                    <Link
                      to={`/clients/${project.clientId}`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        color: 'var(--accent)',
                        fontWeight: 500,
                        textDecoration: 'none',
                      }}
                    >
                      <Building2 size={14} />
                      <span>{project.clientName}</span>
                      <ExternalLink size={11} style={{ opacity: 0.7 }} />
                    </Link>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <Building2 size={14} />
                      <span>{project.clientName}</span>
                    </span>
                  )}

                  {project.location && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <MapPin size={14} style={{ color: 'var(--text-faint)' }} />
                      <span>{project.location}</span>
                    </span>
                  )}

                  {project.startDate && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <Calendar size={14} style={{ color: 'var(--text-faint)' }} />
                      <span>Started {formatDate(project.startDate)}</span>
                    </span>
                  )}
                </div>

                {project.description && (
                  <div
                    style={{
                      fontSize: 13,
                      color: 'var(--text-faint)',
                      marginTop: 10,
                      maxWidth: 720,
                      lineHeight: 1.5,
                      background: 'var(--surface-2)',
                      padding: '8px 12px',
                      borderRadius: 'var(--radius-xs)',
                      border: '1px solid var(--border-subtle)',
                    }}
                  >
                    {project.description}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Financial Summary & Reminders for Admin */}
          {admin && (() => {
            const extraPaise = Math.max(0, (project.paidPaise || 0) - (project.valuePaise || 0));
            return (
              <>
                <div className="kpi-row" style={{ marginBottom: 18 }}>
                  <div className="kpi">
                    <div className="lbl">
                      <span>Project Contract Value</span>
                      <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Briefcase size={14} />
                      </div>
                    </div>
                    <div className="val">{formatMoney(project.valuePaise)}</div>
                    <div className="sub">agreed client budget</div>
                  </div>

                  <div className="kpi">
                    <div className="lbl">
                      <span>Collections Received</span>
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 'var(--radius-xs)',
                          background: '#ecfdf5',
                          color: '#059669',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CheckCircle2 size={14} />
                      </div>
                    </div>
                    <div className="val" style={{ color: '#059669' }}>
                      {formatMoney(project.paidPaise)}
                    </div>
                    <div className="sub">
                      {extraPaise > 0 ? (
                        <span>
                          total recorded payments · <strong style={{ color: '#16a34a', fontWeight: 600 }}>+{formatMoney(extraPaise)} extra</strong>
                        </span>
                      ) : (
                        'total recorded payments'
                      )}
                    </div>
                  </div>

                  <div className="kpi">
                    <div className="lbl">
                      <span>Outstanding Balance</span>
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 'var(--radius-xs)',
                          background: project.pendingPaise > 0 ? '#fff7ed' : (extraPaise > 0 ? '#ecfdf5' : '#f8fafc'),
                          color: project.pendingPaise > 0 ? '#ea580c' : (extraPaise > 0 ? '#059669' : '#64748b'),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <IndianRupee size={14} />
                      </div>
                    </div>
                    <div
                      className="val"
                      style={{ color: project.pendingPaise > 0 ? '#ea580c' : (extraPaise > 0 ? '#059669' : 'var(--text-faint)') }}
                    >
                      {formatMoney(project.pendingPaise)}
                    </div>
                    <div className="sub">
                      {project.pendingPaise > 0
                        ? 'payment collection pending'
                        : extraPaise > 0
                          ? `fully settled (+${formatMoney(extraPaise)} surplus)`
                          : 'fully settled account'}
                    </div>
                  </div>

                  {/* Total Project Expenses Card */}
                  <div className="kpi">
                    <div className="lbl">
                      <span>Total Project Expenses</span>
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 'var(--radius-xs)',
                          background: '#fef2f2',
                          color: '#dc2626',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Receipt size={14} />
                      </div>
                    </div>
                    <div className="val" style={{ color: '#dc2626' }}>
                      {formatMoney(project.totalExpensesPaise)}
                    </div>
                    <div className="sub" style={{ fontSize: 11.5, color: 'var(--text-faint)', lineHeight: 1.4 }}>
                      <span>Mat: <strong style={{ color: 'var(--text)' }}>{formatMoney(project.materialPaidPaise)}</strong></span>
                      {' · '}
                      <span>Sub: <strong style={{ color: 'var(--text)' }}>{formatMoney(project.subcontractorPaidPaise)}</strong></span>
                      {' · '}
                      <span>Exp: <strong style={{ color: 'var(--text)' }}>{formatMoney(project.expenseTotalPaise)}</strong></span>
                      {project.netTransferPaise !== 0 && (
                        <>
                          {' · '}
                          <span>
                            Transfer:{' '}
                            <strong style={{ color: project.netTransferPaise > 0 ? '#16a34a' : '#ea580c' }}>
                              {project.netTransferPaise > 0 ? '+' : ''}{formatMoney(project.netTransferPaise)}
                            </strong>
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Payment Status Banner */}
                <div className={`payment-reminder-banner ${project.pendingPaise > 0 ? 'active' : 'settled'}`}>
                  <div className="payment-reminder-body">
                    <div className="payment-reminder-icon-box">
                      {project.pendingPaise > 0 ? <Bell size={18} /> : <CheckCircle2 size={18} />}
                    </div>
                    <div className="payment-reminder-content">
                      <div className="payment-reminder-text">
                        {project.pendingPaise > 0 ? (
                          <>
                            <strong>Payment Reminder Active:</strong> Daily notifications will notify the admin until the remaining{' '}
                            <strong style={{ color: '#ea580c' }}>{formatMoney(project.pendingPaise)}</strong> is collected.
                          </>
                        ) : extraPaise > 0 ? (
                          <>
                            <strong>Payment Completed:</strong> This project is fully settled with an extra surplus collection of{' '}
                            <strong style={{ color: '#059669' }}>+{formatMoney(extraPaise)}</strong> over the contract value. Reminders have automatically stopped.
                          </>
                        ) : (
                          <>
                            <strong>Payment Completed:</strong> This project is fully paid. Reminders have automatically stopped.
                          </>
                        )}
                      </div>
                      {project.pendingPaise > 0 && canSubmit(actor, 'add_client_payment', id) && (
                        <div className="payment-reminder-action">
                          <button
                            className="btn btn-auto btn-sm btn-primary payment-reminder-btn"
                            onClick={() => setEntry('payment')}
                          >
                            <Plus size={14} />
                            <span>Client payment</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            );
          })()}

          {/* Site Engineer Total Expenses Card */}
          {!admin && (
            <div className="kpi-row" style={{ marginBottom: 18 }}>
              <div className="kpi" style={{ maxWidth: 440 }}>
                <div className="lbl">
                  <span>Total Expenses in this Project</span>
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 'var(--radius-xs)',
                      background: '#fef2f2',
                      color: '#dc2626',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Receipt size={14} />
                  </div>
                </div>
                <div className="val" style={{ color: '#dc2626' }}>
                  {formatMoney(project.totalExpensesPaise)}
                </div>
                <div className="sub" style={{ fontSize: 11.5, color: 'var(--text-faint)' }}>
                  <span>Materials: <strong style={{ color: 'var(--text)' }}>{formatMoney(project.materialPaidPaise)}</strong></span>
                  {' · '}
                  <span>Sub-contractor: <strong style={{ color: 'var(--text)' }}>{formatMoney(project.subcontractorPaidPaise)}</strong></span>
                  {' · '}
                  <span>Other Expenses: <strong style={{ color: 'var(--text)' }}>{formatMoney(project.expenseTotalPaise)}</strong></span>
                  {project.netTransferPaise !== 0 && (
                    <>
                      {' · '}
                      <span>
                        Site Transfers:{' '}
                        <strong style={{ color: project.netTransferPaise > 0 ? '#16a34a' : '#ea580c' }}>
                          {project.netTransferPaise > 0 ? '+' : ''}{formatMoney(project.netTransferPaise)}
                        </strong>
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Quick Operations Bar */}
          {available.length > 0 && (
            <div className="quick-entries-wrapper">
              <div className="quick-entries-title">
                Quick Site Entries
              </div>
              <div className="quick-entries-grid">
                {available.map((a) => {
                  const IconComponent = a.icon;
                  return (
                    <button
                      key={a.kind}
                      className="btn btn-auto btn-sm quick-entry-btn"
                      onClick={() => (
                        a.kind === 'purchase' ? setBillOpen(true) :
                        a.kind === 'usage' ? setUsageOpen(true) :
                        a.kind === 'labour' ? setLabourOpen(true) :
                        a.kind === 'expense' ? setExpenseOpen(true) :
                        a.kind === 'transfer' ? setTransferOpen(true) :
                        setEntry(a.kind)
                      )}
                    >
                      <IconComponent size={14} className="quick-entry-icon" />
                      <span className="quick-entry-label">{a.label}</span>
                    </button>
                  );
                })}

                {canAccessProject(actor, id) && (
                  <button
                    className="btn btn-auto btn-sm quick-entry-btn"
                    onClick={() => setDrawingOpen(true)}
                  >
                    <Layers size={14} className="quick-entry-icon" />
                    <span className="quick-entry-label">Project Drawings</span>
                  </button>
                )}

                {canAccessProject(actor, id) && (
                  <button
                    className="btn btn-auto btn-sm quick-entry-btn"
                    onClick={() => setElevationOpen(true)}
                  >
                    <Sparkles size={14} className="quick-entry-icon" />
                    <span className="quick-entry-label">3D Elevation</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Tabbed Ledgers Card */}
          <div className="card" style={{ overflow: 'hidden' }}>
            <div
              style={{
                padding: '0 16px',
                borderBottom: '1px solid var(--border)',
                background: 'var(--surface-2)',
                overflowX: 'auto',
              }}
            >
              <div style={{ display: 'flex', gap: 18, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: 18 }}>
                  {tabs.map((t) => {
                    const TabIcon = t.icon;
                    const active = tab === t.key;
                    return (
                      <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        style={{
                          padding: '12px 2px',
                          fontSize: 13,
                          fontWeight: active ? 600 : 500,
                          cursor: 'pointer',
                          fontFamily: 'var(--font-ui)',
                          background: 'none',
                          border: 'none',
                          whiteSpace: 'nowrap',
                          color: active ? 'var(--accent)' : 'var(--text-faint)',
                          borderBottom: `2px solid ${active ? 'var(--accent)' : 'transparent'}`,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <TabIcon size={14} />
                        <span>{t.label}</span>
                      </button>
                    );
                  })}
                </div>

                {tab === 'transfers' && canAccessProject(actor, id) && (
                  <button
                    className="btn btn-primary btn-auto btn-sm"
                    onClick={() => setTransferOpen(true)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      fontSize: 12,
                      padding: '4px 10px',
                      borderRadius: 'var(--radius-xs)',
                      margin: '6px 0',
                    }}
                  >
                    <Repeat size={13} />
                    <span>Add Stock Transfer</span>
                  </button>
                )}
              </div>
            </div>
            <Ledger
              projectId={id}
              projectName={project?.name}
              tab={tab}
              admin={admin}
              onEdit={setEditRow}
              onEditSubcontractor={setSubcontractorEditRow}
              onDeleteSubcontractor={setSubcontractorDeleteRow}
            />
          </div>
        </motion.div>
      )}

      {entry && (entry !== 'payment' || admin) && (
        <EntryDrawer kind={entry} projectId={id} onClose={() => setEntry(null)} />
      )}
      {billOpen && <BillDrawer projectId={id} onClose={() => setBillOpen(false)} />}
      {usageOpen && <UsageDrawer projectId={id} onClose={() => setUsageOpen(false)} />}
      {(labourOpen || subcontractorEditRow) && (
        <SubcontractorTransactionDrawer
          projectId={id}
          existing={subcontractorEditRow}
          onClose={() => {
            setLabourOpen(false);
            setSubcontractorEditRow(null);
          }}
        />
      )}
      {expenseOpen && <ExpenseDrawer projectId={id} onClose={() => setExpenseOpen(false)} />}
      {transferOpen && (
        <StockTransferDrawer
          fromProjectId={id}
          fromProjectName={project?.name}
          onClose={() => setTransferOpen(false)}
        />
      )}
      {drawingOpen && (
        <DrawingUploadDrawer
          projectId={id}
          projectName={project?.name}
          onClose={() => setDrawingOpen(false)}
        />
      )}
      {elevationOpen && (
        <ElevationUploadDrawer
          projectId={id}
          projectName={project?.name}
          onClose={() => setElevationOpen(false)}
        />
      )}

      {editRow && (
        <EntryDrawer
          kind={TAB_KIND[tab]}
          projectId={id}
          existing={editRow}
          onClose={() => setEditRow(null)}
          onDelete={() => setDeleteRow(editRow)}
        />
      )}

      {deleteRow && (
        <Confirm
          title="Delete this entry?"
          message="Totals on this project, the dashboard and every report will update straight away. You can undo it from the message that appears."
          busy={removeEntry.isPending}
          onConfirm={() => void doDeleteEntry()}
          onCancel={() => setDeleteRow(null)}
        />
      )}

      {subcontractorDeleteRow && (
        <Confirm
          title="Delete this sub contractor entry?"
          message="This will remove the labour entry and update project totals and balances straight away."
          busy={deleteSubcontractorTx.isPending}
          onConfirm={async () => {
            if (!subcontractorDeleteRow) return;
            try {
              await deleteSubcontractorTx.mutateAsync(subcontractorDeleteRow);
              toast.show('Sub contractor entry deleted');
            } catch (err) {
              toast.show(err instanceof Error ? err.message : 'Could not delete entry', true);
            } finally {
              setSubcontractorDeleteRow(null);
            }
          }}
          onCancel={() => setSubcontractorDeleteRow(null)}
        />
      )}
    </Shell>
  );
}

/* ------------------------------- Ledgers Table ------------------------------- */

type Row = Record<string, unknown>;
const rel = (v: unknown, key = 'name'): string =>
  (v as Record<string, string> | null)?.[key] ?? '—';
const qty = (n: unknown) => Number(n).toLocaleString('en-IN', { maximumFractionDigits: 3 });

function Ledger({
  projectId,
  projectName,
  tab,
  admin,
  onEdit,
  onEditSubcontractor,
  onDeleteSubcontractor,
}: {
  projectId: string;
  projectName?: string;
  tab: LedgerTab;
  admin: boolean;
  onEdit: (row: ExistingEntry) => void;
  onEditSubcontractor: (row: SubcontractorTxRow) => void;
  onDeleteSubcontractor: (row: SubcontractorTxRow) => void;
}) {
  if (tab === 'drawings') {
    return <ProjectDrawingsLedger projectId={projectId} projectName={projectName} />;
  }
  if (tab === 'elevations') {
    return <ProjectElevationsLedger projectId={projectId} projectName={projectName} />;
  }
  if (tab === 'labour') {
    return (
      <ProjectSubcontractorLedger
        projectId={projectId}
        admin={admin}
        onEdit={onEditSubcontractor}
        onDelete={onDeleteSubcontractor}
      />
    );
  }

  const { data, isLoading, error, refetch } = useLedger(projectId, tab);

  if (isLoading) return <div className="table-wrap"><TableSkeleton rows={4} cols={5} /></div>;
  if (error) return <div className="table-wrap"><ErrorState error={error} onRetry={() => void refetch()} /></div>;

  const rows = (data ?? []) as Row[];
  if (!rows.length) {
    return (
      <div className="table-wrap">
        <EmptyState
          icon="filetext"
          title="Nothing recorded yet"
          hint="Use the quick entry buttons above to post the first transaction."
        />
      </div>
    );
  }

  return <div className="table-wrap">{renderLedger(tab, rows, admin, projectId, onEdit)}</div>;
}

function renderLedger(
  tab: LedgerTab,
  rows: Row[],
  admin: boolean,
  projectId: string,
  onEdit: (row: ExistingEntry) => void,
) {
  const rowProps = (r: Row) =>
    admin
      ? { className: 'clickable', onClick: () => onEdit(r as ExistingEntry) }
      : {};

  switch (tab) {
    case 'payments': {
      const total = rows.reduce((s, r) => s + Number(r.amount_paise), 0);
      return (
        <table>
          <thead>
            <tr>
              <th style={{ textAlign: 'center' }}>Date</th>
              <th style={{ textAlign: 'center' }}>Payment Type</th>
              <th style={{ textAlign: 'center' }}>Amount</th>
              <th style={{ textAlign: 'center' }}>Reference No</th>
              <th style={{ textAlign: 'center' }}>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={String(r.id)} {...rowProps(r)}>
                <td style={{ textAlign: 'center' }}>{formatDate(r.paid_on as string)}</td>
                <td style={{ textAlign: 'center', textTransform: 'capitalize' }}>
                  {String(r.payment_type).replace('_', ' ')}
                </td>
                <td className="num" style={{ textAlign: 'center', color: '#059669', fontWeight: 600 }}>
                  {formatMoney(Number(r.amount_paise))}
                </td>
                <td className="num" style={{ textAlign: 'center' }}>
                  {(r.reference_no as string) || '—'}
                </td>
                <td style={{ textAlign: 'center' }}>{cleanDisplayRemarks(r.remarks as string) || '—'}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2} style={{ textAlign: 'center', fontWeight: 700 }}>
                Total Received
              </td>
              <td className="num" style={{ textAlign: 'center', fontWeight: 700, color: '#059669' }}>
                {formatMoney(total)}
              </td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
      );
    }

    case 'purchases': {
      const total = rows.reduce((s, r) => s + Number(r.amount_paise), 0);
      const advance = rows.reduce((s, r) => s + Number(r.advance_paid_paise), 0);
      return (
        <table>
          <thead>
            <tr>
              <th style={{ textAlign: 'center' }}>Date</th>
              <th style={{ textAlign: 'center' }}>Material</th>
              <th style={{ textAlign: 'center' }}>Supplier</th>
              <th style={{ textAlign: 'center' }}>Quantity</th>
              <th style={{ textAlign: 'center' }}>Unit</th>
              {admin && <th style={{ textAlign: 'center' }}>Rate</th>}
              {admin && <th style={{ textAlign: 'center' }}>Total Amount</th>}
              {admin && <th style={{ textAlign: 'center' }}>Advance Paid</th>}
              {admin && <th style={{ textAlign: 'center' }}>Balance Due</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const amount = Number(r.amount_paise);
              const adv = Number(r.advance_paid_paise);
              return (
                <tr key={String(r.id)} {...rowProps(r)}>
                  <td style={{ textAlign: 'center' }}>{formatDate(r.purchased_on as string)}</td>
                  <td className="name" style={{ textAlign: 'center' }}>{rel(r.materials)}</td>
                  <td style={{ textAlign: 'center' }}>{rel(r.suppliers)}</td>
                  <td className="num" style={{ textAlign: 'center' }}>{qty(r.quantity)}</td>
                  <td style={{ textAlign: 'center' }}>{rel(r.units, 'short_name')}</td>
                  {admin && (
                    <td className="num" style={{ textAlign: 'center' }}>
                      {formatMoney(Number(r.rate_paise))}
                    </td>
                  )}
                  {admin && (
                    <td className="num" style={{ textAlign: 'center', fontWeight: 600 }}>
                      {formatMoney(amount)}
                    </td>
                  )}
                  {admin && (
                    <td className="num" style={{ textAlign: 'center', color: '#059669' }}>
                      {formatMoney(adv)}
                    </td>
                  )}
                  {admin && (
                    <td
                      className="num"
                      style={{
                        textAlign: 'center',
                        color: amount - adv > 0 ? '#ea580c' : 'var(--text-faint)',
                        fontWeight: amount - adv > 0 ? 600 : 400,
                      }}
                    >
                      {formatMoney(amount - adv)}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          {admin && (
            <tfoot>
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', fontWeight: 700 }}>
                  Total
                </td>
                <td className="num" style={{ textAlign: 'center', fontWeight: 700 }}>
                  {formatMoney(total)}
                </td>
                <td className="num" style={{ textAlign: 'center', fontWeight: 700, color: '#059669' }}>
                  {formatMoney(advance)}
                </td>
                <td className="num" style={{ textAlign: 'center', fontWeight: 700, color: '#ea580c' }}>
                  {formatMoney(total - advance)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      );
    }

    case 'usage':
      return (
        <table>
          <thead>
            <tr>
              <th style={{ textAlign: 'center' }}>Date</th>
              <th style={{ textAlign: 'center' }}>Material</th>
              <th style={{ textAlign: 'center' }}>Quantity Used</th>
              <th style={{ textAlign: 'center' }}>Quantity Remaining</th>
              <th style={{ textAlign: 'center' }}>Unit</th>
              <th style={{ textAlign: 'center' }}>Activity / Location</th>
              <th style={{ textAlign: 'center' }}>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={String(r.id)} {...rowProps(r)}>
                <td style={{ textAlign: 'center' }}>{formatDate(r.used_on as string)}</td>
                <td className="name" style={{ textAlign: 'center' }}>{rel(r.materials)}</td>
                <td className="num" style={{ textAlign: 'center', fontWeight: 600 }}>
                  {qty(r.quantity)}
                </td>
                <td
                  className="num"
                  style={{
                    textAlign: 'center',
                    fontWeight: 600,
                    color: Number(r.remaining_quantity) <= 0 ? 'var(--text-faint)' : '#059669',
                  }}
                >
                  {qty(r.remaining_quantity ?? 0)}
                </td>
                <td style={{ textAlign: 'center' }}>{rel(r.units, 'short_name')}</td>
                <td style={{ textAlign: 'center' }}>{(r.activity as string) || '—'}</td>
                <td style={{ textAlign: 'center' }}>{cleanDisplayRemarks(r.remarks as string) || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );

    case 'labour':
      return null;

    case 'expenses': {
      const total = rows.reduce((s, r) => s + Number(r.amount_paise), 0);
      return (
        <table>
          <thead>
            <tr>
              <th style={{ textAlign: 'center' }}>Date</th>
              <th style={{ textAlign: 'center' }}>Expense Title</th>
              <th style={{ textAlign: 'center' }}>Category</th>
              <th style={{ textAlign: 'center' }}>Amount</th>
              <th style={{ textAlign: 'center' }}>Paid To</th>
              <th style={{ textAlign: 'center' }}>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={String(r.id)} {...rowProps(r)}>
                <td style={{ textAlign: 'center' }}>{formatDate(r.spent_on as string)}</td>
                <td className="name" style={{ textAlign: 'center' }}>{String(r.title)}</td>
                <td style={{ textAlign: 'center' }}>{rel(r.expense_categories)}</td>
                <td className="num" style={{ textAlign: 'center', color: '#ea580c', fontWeight: 600 }}>
                  {formatMoney(Number(r.amount_paise))}
                </td>
                <td style={{ textAlign: 'center' }}>{(r.paid_to as string) || '—'}</td>
                <td style={{ textAlign: 'center' }}>{cleanDisplayRemarks(r.remarks as string) || '—'}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3} style={{ textAlign: 'center', fontWeight: 700 }}>
                Total Expenses
              </td>
              <td className="num" style={{ textAlign: 'center', fontWeight: 700, color: '#ea580c' }}>
                {formatMoney(total)}
              </td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
      );
    }

    case 'transfers': {
      const totalSentOut = rows
        .filter((r) => r.from_project_id === projectId)
        .reduce((s, r) => {
          const val = r.amount_paise != null ? Number(r.amount_paise) : (Number(r.quantity || 0) * Number(r.rate_paise || 0));
          return s + (val || 0);
        }, 0);

      const totalReceived = rows
        .filter((r) => r.to_project_id === projectId)
        .reduce((s, r) => {
          const val = r.amount_paise != null ? Number(r.amount_paise) : (Number(r.quantity || 0) * Number(r.rate_paise || 0));
          return s + (val || 0);
        }, 0);

      const netTransfer = totalReceived - totalSentOut;

      return (
        <table>
          <thead>
            <tr>
              <th style={{ textAlign: 'center' }}>Date</th>
              <th style={{ textAlign: 'center' }}>Transfer Direction</th>
              <th style={{ textAlign: 'center' }}>Material</th>
              <th style={{ textAlign: 'center' }}>Quantity</th>
              <th style={{ textAlign: 'center' }}>Unit</th>
              <th style={{ textAlign: 'center' }}>Rate</th>
              <th style={{ textAlign: 'center' }}>Total Amount</th>
              <th style={{ textAlign: 'center' }}>From / To Site</th>
              <th style={{ textAlign: 'center' }}>Vehicle No</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const outgoing = r.from_project_id === projectId;
              const ratePaise = r.rate_paise != null ? Number(r.rate_paise) : null;
              const amountPaise = r.amount_paise != null ? Number(r.amount_paise) : (ratePaise != null && r.quantity != null ? ratePaise * Number(r.quantity) : null);
              return (
                <tr key={String(r.id)} {...rowProps(r)}>
                  <td style={{ textAlign: 'center' }}>{formatDate(r.transferred_on as string)}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 'var(--radius-xs)',
                        fontSize: 11.5,
                        fontWeight: 600,
                        background: outgoing ? '#fff7ed' : '#ecfdf5',
                        color: outgoing ? '#c2410c' : '#059669',
                        border: `1px solid ${outgoing ? '#ffedd5' : '#d1fae5'}`,
                      }}
                    >
                      {outgoing ? 'Sent Out' : 'Received'}
                    </span>
                  </td>
                  <td className="name" style={{ textAlign: 'center' }}>{rel(r.materials)}</td>
                  <td className="num" style={{ textAlign: 'center', fontWeight: 600 }}>
                    {qty(r.quantity)}
                  </td>
                  <td style={{ textAlign: 'center' }}>{rel(r.units, 'short_name')}</td>
                  <td className="num" style={{ textAlign: 'center' }}>
                    {ratePaise != null ? formatMoney(ratePaise) : '—'}
                  </td>
                  <td className="num" style={{ textAlign: 'center', fontWeight: 600, color: amountPaise ? 'var(--text)' : 'var(--text-faint)' }}>
                    {amountPaise != null ? formatMoney(amountPaise) : '—'}
                  </td>
                  <td style={{ textAlign: 'center' }}>{outgoing ? rel(r.to_project) : rel(r.from_project)}</td>
                  <td className="num" style={{ textAlign: 'center' }}>
                    {(r.vehicle_no as string) || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={6} style={{ textAlign: 'right', fontWeight: 700, paddingRight: 14 }}>
                  Total Sent Out (Outgoing):
                </td>
                <td className="num" style={{ textAlign: 'center', fontWeight: 700, color: '#c2410c' }}>
                  {formatMoney(totalSentOut)}
                </td>
                <td colSpan={2} />
              </tr>
              <tr>
                <td colSpan={6} style={{ textAlign: 'right', fontWeight: 700, paddingRight: 14 }}>
                  Total Received (Incoming):
                </td>
                <td className="num" style={{ textAlign: 'center', fontWeight: 700, color: '#059669' }}>
                  {formatMoney(totalReceived)}
                </td>
                <td colSpan={2} />
              </tr>
              <tr style={{ background: 'var(--surface-muted)' }}>
                <td colSpan={6} style={{ textAlign: 'right', fontWeight: 800, paddingRight: 14 }}>
                  Net Transfer Value:
                </td>
                <td className="num" style={{ textAlign: 'center', fontWeight: 800, color: netTransfer >= 0 ? '#059669' : '#c2410c' }}>
                  {netTransfer > 0 ? '+' : ''}{formatMoney(netTransfer)}
                </td>
                <td colSpan={2} style={{ textAlign: 'left', fontSize: 11.5, color: 'var(--text-faint)' }}>
                  {netTransfer > 0 ? '(Net incoming stock)' : netTransfer < 0 ? '(Net outgoing stock)' : '(Balanced)'}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      );
    }
  }
}

function ProjectSubcontractorLedger({
  projectId,
  admin,
  onEdit,
  onDelete,
}: {
  projectId: string;
  admin: boolean;
  onEdit: (row: SubcontractorTxRow) => void;
  onDelete: (row: SubcontractorTxRow) => void;
}) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const { data: rows, isLoading, error, refetch } = useSubcontractorTransactions('', projectId);

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="table-wrap">
        <TableSkeleton rows={4} cols={11} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="table-wrap">
        <ErrorState error={error} onRetry={() => void refetch()} />
      </div>
    );
  }

  const list = rows ?? [];
  if (!list.length) {
    return (
      <div className="table-wrap">
        <EmptyState
          icon="filetext"
          title="No sub contractor records yet"
          hint="Click 'Sub contractor payment' above to record daily muster rolls, individual piece-rate work, or payments."
        />
      </div>
    );
  }

  const totalCredit = list.reduce((s, r) => s + r.overallTotalPaise, 0);
  const totalDebit = list.reduce((s, r) => s + r.paidAmountPaise, 0);
  const totalBalanceDue = Math.max(0, totalCredit - totalDebit);

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th style={{ width: 36, textAlign: 'center' }}></th>
            <th style={{ textAlign: 'center' }}>Date</th>
            <th style={{ textAlign: 'center' }}>Sub-contractor</th>
            <th style={{ textAlign: 'center' }}>Work Trades</th>
            <th style={{ textAlign: 'center' }}>Workers</th>
            <th style={{ textAlign: 'center' }}>Credit (₹)</th>
            <th style={{ textAlign: 'center' }}>Debit (₹)</th>
            <th style={{ textAlign: 'center' }}>Balance Due (₹)</th>
            <th style={{ textAlign: 'center' }}>Remarks</th>
            <th style={{ textAlign: 'center' }}>Status</th>
            <th style={{ textAlign: 'center', width: 80 }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {list.map((r) => {
            const isExpanded = expandedRows.has(r.id);
            const isZeroBalance = r.balanceAmountPaise <= 0;
            const hasLines = r.lines && r.lines.length > 0;
            const cleanedRemarks = cleanDisplayRemarks(r.remarks);

            return (
              <Fragment key={r.id}>
                <tr>
                  <td style={{ textAlign: 'center', padding: '6px 4px' }}>
                    {hasLines ? (
                      <button
                        type="button"
                        onClick={() => toggleRow(r.id)}
                        className="icon-btn"
                        style={{ width: 26, height: 26, padding: 0, margin: '0 auto' }}
                        title={isExpanded ? 'Collapse trade lines' : 'Expand trade lines'}
                      >
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </button>
                    ) : (
                      <span style={{ color: 'var(--text-faint)', fontSize: 11 }}>—</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center', fontSize: 12.5, whiteSpace: 'nowrap' }}>
                    {formatDate(r.date)}
                  </td>
                  <td style={{ textAlign: 'center', fontWeight: 600 }}>
                    {r.subcontractorName}
                  </td>
                  <td style={{ textAlign: 'center', fontSize: 12.5 }}>
                    <span>{r.tradesSummary}</span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {r.totalWorkers > 0 ? (
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 12,
                          fontSize: 12,
                          fontWeight: 700,
                          background: 'rgba(217, 119, 6, 0.1)',
                          color: '#d97706',
                        }}
                      >
                        {r.totalWorkers} {r.totalWorkers === 1 ? 'worker' : 'workers'}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td
                    className="num"
                    style={{
                      textAlign: 'center',
                      fontWeight: 600,
                      fontFamily: 'var(--font-mono)',
                      fontSize: 13,
                    }}
                  >
                    {r.overallTotalPaise > 0 ? formatMoney(r.overallTotalPaise) : '—'}
                  </td>
                  <td
                    className="num"
                    style={{
                      textAlign: 'center',
                      fontWeight: 600,
                      fontFamily: 'var(--font-mono)',
                      fontSize: 13,
                      color: r.paidAmountPaise > 0 ? '#059669' : 'var(--text-faint)',
                    }}
                  >
                    {r.paidAmountPaise > 0 ? formatMoney(r.paidAmountPaise) : '—'}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 'var(--radius-xs)',
                        fontSize: 12,
                        fontFamily: 'var(--font-mono)',
                        fontWeight: 600,
                        background: isZeroBalance ? 'rgba(5, 150, 105, 0.12)' : 'rgba(220, 38, 38, 0.12)',
                        color: isZeroBalance ? '#059669' : '#dc2626',
                      }}
                    >
                      {isZeroBalance ? '₹0' : formatMoney(r.balanceAmountPaise)}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center', fontSize: 12.5, color: 'var(--text-soft)', maxWidth: 200 }}>
                    {cleanedRemarks || '—'}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <StatusChip value={r.status} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                      <button
                        type="button"
                        onClick={() => onEdit(r)}
                        className="icon-btn"
                        title="Edit entry"
                        style={{ width: 28, height: 28, padding: 0 }}
                      >
                        <Edit2 size={13} />
                      </button>
                      {admin && (
                        <button
                          type="button"
                          onClick={() => onDelete(r)}
                          className="icon-btn text-danger"
                          title="Delete entry"
                          style={{ width: 28, height: 28, padding: 0 }}
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>

                {isExpanded && hasLines && (
                  <tr style={{ background: 'var(--surface-2)' }}>
                    <td colSpan={11} style={{ padding: '10px 16px 14px 44px' }}>
                      <div
                        style={{
                          background: '#ffffff',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)',
                          overflow: 'hidden',
                          boxShadow: 'var(--shadow-xs)',
                        }}
                      >
                        <div
                          style={{
                            padding: '6px 12px',
                            background: 'var(--surface-3)',
                            borderBottom: '1px solid var(--border)',
                            fontSize: 12,
                            fontWeight: 700,
                            color: 'var(--text-soft)',
                          }}
                        >
                          Work Trade Headcounts & Daily Rates
                        </div>
                        <table style={{ margin: 0 }}>
                          <thead>
                            <tr style={{ background: '#fafafa', borderBottom: '1px solid var(--border)' }}>
                              <th style={{ fontSize: 11.5 }}>Work Trade</th>
                              <th style={{ fontSize: 11.5, textAlign: 'center' }}>Workers</th>
                              <th style={{ fontSize: 11.5, textAlign: 'center' }}>Daily Rate (₹)</th>
                              <th style={{ fontSize: 11.5, textAlign: 'center' }}>Total Amount</th>
                              <th style={{ fontSize: 11.5 }}>Remarks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {r.lines.map((l, i) => (
                              <tr key={i}>
                                <td style={{ fontWeight: 600, fontSize: 12.5 }}>{cleanWorkTypeName(l.workTypeName)}</td>
                                <td style={{ textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>
                                  {l.workerCount}
                                </td>
                                <td style={{ textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>
                                  {l.ratePerWorkerPaise > 0 ? formatMoney(l.ratePerWorkerPaise) : '—'}
                                </td>
                                <td style={{ textAlign: 'center', fontWeight: 600, fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>
                                  {formatMoney(l.totalAmountPaise)}
                                </td>
                                <td style={{ fontSize: 12, color: 'var(--text-soft)' }}>
                                  {cleanDisplayRemarks(l.remarks) || '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={5} style={{ textAlign: 'center', fontWeight: 700 }}>
              Total Sub-contractor Work & Outlays
            </td>
            <td className="num" style={{ textAlign: 'center', fontWeight: 700 }}>
              {formatMoney(totalCredit)}
            </td>
            <td className="num" style={{ textAlign: 'center', fontWeight: 700, color: '#059669' }}>
              {formatMoney(totalDebit)}
            </td>
            <td className="num" style={{ textAlign: 'center', fontWeight: 700, color: totalBalanceDue > 0 ? '#dc2626' : '#059669' }}>
              {formatMoney(totalBalanceDue)}
            </td>
            <td colSpan={3} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}


